<script setup lang="ts">
import { ecommerce } from '@/composables/dataLayerUtil'
import { useModalStore } from '@/store/modalStore'
import type { Product, Variant, VariantColor } from '~/types/product.type'
import AppListbox from '@/components/app/AppListbox.vue'

const authStore = useAuthStore()
const cartStore = useShoppingCartStore()
const isB2b = computed(() => authStore.isB2B)

const productDetailStore = useProductDetailStore()
const currentProduct = computed(() => productDetailStore.product)

const modalStore = useModalStore()
const {
  t,
  locale,
} = useI18n()

const selectedVariant = ref<Variant | null>(null)
const selectedColor = ref<VariantColor | null>(null)
const amount = ref<number>(1)

function show3d() {
  modalStore.image3dModalShown = true
  modalStore.image = currentProduct.value?.['360_images'][0] ?? '' as any
}

function remind() {
  modalStore.noStockModalShown = true
  modalStore.noStockProduct = selectedVariant.value as any
}

const canOrder = computed(() => {
  // @ts-expect-error - dont wanna break amount.value !== ''
  return selectedVariant.value && amount.value !== null && amount.value !== '' && amount.value > 0 && selectedVariant?.value?.stock >= Number.parseInt(amount.value)
})

function addToShoppingCart() {
  // @ts-expect-error - Unsure
  cartStore.addProduct(selectedVariant.value.id, amount.value)
  modalStore.setProductAdded(selectedVariant.value)
  // @ts-expect-error - Unsure
  modalStore.showProductModal(true)

  if (!selectedVariant.value)
    return

  ecommerce('add_to_cart', {
    currency: 'EUR',
    value: (amount.value * selectedVariant.value.price / 100)?.toFixed(2),
    items: [
      {
        item_id: selectedVariant.value.code,
        item_name: selectedVariant.value.title,
        price: selectedVariant.value.price?.toFixed(2),
        quantity: amount.value,
      },
    ],
  })
}

onMounted(() => {
  if (!currentProduct.value)
    return
  ecommerce('view_item_list', {
    item_list_id: currentProduct.value.code,
    item_list_name: currentProduct.value.title,
    items: currentProduct.value.variants.map((item) => {
      return {
        item_id: item.code,
        item_name: item.title,
        price: (item.price / 100)?.toFixed(2),
      }
    }),
  })
})

function setColor(color: VariantColor) {
  if (color.title === selectedColor.value?.title && color.code === selectedColor.value?.code)
    selectedColor.value = null
  else
    selectedColor.value = color
}

watch(selectedVariant, (newVariant) => {
  if (newVariant) {
    ecommerce('view_item', {
      currency: 'EUR',
      value: (newVariant.price / 100)?.toFixed(2),
      items: [
        {
          item_id: newVariant.code,
          item_name: newVariant.title,
          price: (newVariant.price / 100)?.toFixed(2),
        },
      ],
    })
  }
})

const variants = computed(() => {
  if (!selectedColor.value)
    return productDetailStore.product?.variants
  return productDetailStore.product?.variants.filter(x => x.color?.title === selectedColor.value?.title && x.color?.code === selectedColor.value?.code)
})

watch(variants, (newList) => {
  if (newList?.find(x => x.id === selectedVariant.value?.id))
    return
  selectedVariant.value = null
})

const imageList = computed(() => {
  if (!selectedVariant.value)
    return currentProduct.value?.images
  if (selectedVariant.value.images.length === 0)
    return currentProduct.value?.images
  return selectedVariant.value.images
})

const productAmountOptions = computed(() => {
  if (selectedVariant.value) {
    let maxAmount = 1
    const b2bStep = selectedVariant.value?.stock_data.b2b_step || 1
    const b2cStep = selectedVariant.value?.stock_data.b2c_step || 1

    maxAmount = Math.max(maxAmount, selectedVariant.value?.stock_data.available)

    let optionsCount

    if (isB2b.value)
      optionsCount = Math.ceil(maxAmount / (b2bStep || 1))
    else
      optionsCount = Math.ceil(maxAmount / (b2cStep || 1))

    return Array.from({ length: optionsCount }, (_, i) => {
      let value
      if (isB2b.value)
        value = (i + 1) * b2bStep
      else
        value = (i + 1) * b2cStep

      return {
        value,
        label: value.toString(),
      }
    })
  }
  else {
    return null
  }
})

watch(() => productAmountOptions.value, (newOptions) => {
  if (newOptions !== null)
    amount.value = newOptions[0].value
})

const route = useRoute()
const code = computed(() => route.query.code)

function selectedVariantFromCode() {
  const foundVariant = variants.value?.find(x => x.code === code.value)
  if (foundVariant)
    selectedVariant.value = foundVariant
}

selectedVariantFromCode()
</script>

<template>
  <div v-if="currentProduct" class="pt-4">
    <div class="relative z-10 flex w-full flex-col overflow-visible rounded lg:flex-row">
      <div class="relative z-30 flex w-full flex-col px-4 lg:w-1/2 gap-2">
        <div class="w-full rounded-lg">
          <ProductPart1Images v-if="imageList?.length" :images="imageList" />

          <img
            v-else
            class="shadow-input group-hover:shadow-input-big group-hover:-translate-y-1 p-30 w-full transform rounded-lg object-contain opacity-50 transition-all duration-500"
            src="~/assets/images/giardino-logo.svg"
            alt="Giardino logo"
          >
          <div class="flex-center flex w-full flex-wrap">
            <div class="mt-4 max-w-max">
              <PaintButton
                v-if="currentProduct['360_images'] && currentProduct['360_images'].length !== 0"
                button-text="3D Fotos"
                class="button mt-4 max-w-max"
                @button-clicked="show3d"
              />
            </div>
          </div>
        </div>

        <ProductSpecifications class="bg-background flex w-full flex-col justify-start rounded-lg p-4" />
        <ProductDownloads class="bg-background flex w-full flex-col justify-start rounded-lg p-4 mb-4" />
      </div>

      <div class="w-full lg:w-1/2 gap-2 flex flex-col">
        <div id="select_variant" class="bg-background relative flex w-full flex-col justify-start gap-8 rounded-lg p-8">
          <h1 class="text-2xl font-bold">
            {{ currentProduct.title }}
          </h1>

          <div
            v-if="productDetailStore.possibleColors.length > 1"
            class="flex flex-col gap-2"
          >
            <div class="text-lg font-bold">
              {{ t("product.color") }}:
            </div>

            <div class="grid md:grid-cols-2 xl:grid-cols-3 md:gap-2 w-full">
              <button
                v-for="color in productDetailStore.possibleColors"
                :key="color.title"
                class="hover:bg-sand bg-white shadow-input focus:ring-primary border-transparant flex-center mb-2 flex cursor-pointer flex-col gap-2 rounded-lg border bg-white p-4 transition-all duration-300"
                :class="
                  [
                    color.title === selectedColor?.title ? 'border-primary' : 'border-transparent',
                  ]"
                @click="setColor(color)"
              >
                <div
                  v-if="color.code"
                  class="h-8 w-8 rounded-full"
                  :style="`background-color:${color.code}`"
                />
                <div
                  v-else
                  class="h-12 w-12 i-guidance-forbidden-2 text-black/30"
                />
                <div class="flex flex-col gap-0">
                  <div class="line-height-tight">
                    {{ color?.title.split('-')?.[0]?.trim() }}
                  </div>
                  <div class="line-height-tight">
                    {{ color?.title.split('-')?.[1]?.trim() }}
                  </div>
                </div>
              </button>
            </div>
          </div>

          <div class="mt-4 flex flex-col gap-2">
            <div class="relative">
              <div class="z-1 bg-background absolute inset-x-0 top-0 -mx-4 -mt-9 h-9">
                <div class="px-4 text-lg font-bold">
                  {{ t("product.variants") }}:
                </div>
              </div>

              <div v-if="variants" v-auto-animate class="max-h-128 -ml-4 -mr-8 flex flex-col overflow-y-auto overflow-x-hidden pl-4 pr-8 py-3 relative">
                <AppListbox v-model="selectedVariant" :items="variants" :display-function="item => item?.title ?? ''" item-key="title">
                  <template #value="{ model }">
                    <div
                      v-if="model"
                      class="text-left border-transparant mb-2 flex w-full cursor-pointer gap-4 rounded-lg p-4 transition-all duration-300"
                    >
                      <div
                        class="shadow-input h-12 w-12 flex-none rounded-full"
                        :style="`background-color:${model.color?.code ?? '#fdf9f5'}`"
                      />
                      <div>
                        <div class="mr-1 text-wrap">
                          {{ model.title }}
                        </div>
                        <div class="opacity-50">
                          ({{ model?.code }} - {{ model.bar_code }})
                        </div>
                        <ul class="list-disc text-sm opacity-50">
                          <template
                            v-for="dimension in currentProduct.relevant_dimensions"
                            :key="dimension + model.code"
                          >
                            <li v-if="model[dimension as keyof Variant] > 0">
                              {{ t(`dimensions.${dimension}.long`) }}:
                              <!-- @vue-expect-error - dont wanna break -->
                              {{ convertToDesiredUnit(model[dimension as keyof Variant], currentProduct.dimension_units[dimension as keyof Product['dimension_units']] ?? currentProduct.dimension_units.default) }}
                            </li>
                          </template>
                        </ul>
                        <price-item
                          :base-price="model.base_price"
                          :is-b2b="isB2b"
                          :price="model.price"
                        />
                      </div>
                    </div>
                  </template>
                  <template #option="{ option }">
                    <div class="text-left border-transparant mb-2 flex w-full cursor-pointer gap-4 rounded-lg p-4 transition-all duration-300">
                      <div
                        class="shadow-input h-12 w-12 flex-none rounded-full"
                        :style="`background-color:${option.color?.code ?? '#fdf9f5'}`"
                      />
                      <div>
                        <div class="mr-1 text-wrap">
                          {{ option.title }}
                        </div>
                        <div class="opacity-50">
                          ({{ option?.code }} - {{ option.bar_code }})
                        </div>
                        <ul class="list-disc text-sm opacity-50">
                          <template
                            v-for="dimension in currentProduct.relevant_dimensions"
                            :key="dimension + option.code"
                          >
                            <li v-if="option[dimension as keyof Variant] > 0">
                              {{ t(`dimensions.${dimension}.long`) }}:
                              <!-- @vue-expect-error - dont wanna break -->
                              {{ convertToDesiredUnit(option[dimension as keyof Variant], currentProduct.dimension_units[dimension as keyof Product['dimension_units']] ?? currentProduct.dimension_units.default) }}
                            </li>
                          </template>
                        </ul>
                        <price-item
                          :base-price="option.base_price"
                          :is-b2b="isB2b"
                          :price="option.price"
                        />
                      </div>
                    </div>
                  </template>
                </AppListbox>
              </div>
            </div>
          </div>

          <div class="flex flex-col gap-2">
            <div class="text-lg font-bold">
              {{ t("product.amount") }}:
            </div>

            <PrimeDropdown
              v-model="amount"
              :disabled="productAmountOptions === null"
              :virtual-scroller-options="{ itemSize: 50 }"
              :options="productAmountOptions"
              :option-label="(option: any) => option.label"
              :option-value="(option: any) => option.value"
              :placeholder="$t('product.quantity')"
            />
          </div>

          <div class="flex flex-col gap-2">
            <div
              v-if="selectedVariant"
              class="bg-sand rounded-lg p-4"
            >
              <div v-if="(selectedVariant?.stock > 0)">
                <div class="flex flex-col gap-2">
                  <div class="text-lg font-bold">
                    {{ t("product.total") }}:
                  </div>
                </div>
                <div class="mt-4 flex flex-col justify-between gap-4">
                  <div class="text-2xl font-bold">
                    <price-item
                      :amount="amount"
                      :base-price="selectedVariant.base_price"
                      :is-b2b="isB2b"
                      :price="selectedVariant.price"
                    />
                  </div>
                </div>

                <div
                  class="transition"
                  :class="[selectedVariant?.stock < parseInt(amount as any) ? 'opacity-50' : 'opacity-0']"
                >
                  {{ t("product.not_enough_stock", { stock: selectedVariant?.stock }) }}
                </div>

                <div class="flex flex-col gap-4 text-center">
                  <PaintButton
                    v-if="selectedVariant?.stock >= parseInt(amount as any)"
                    :button-text="t('product.cart')"
                    :disabled="!canOrder"
                    @button-clicked="addToShoppingCart"
                  />
                  <div v-if="selectedVariant?.stock < parseInt(amount as any)">
                    <PaintButton
                      :button-text="t('product.let_me_know')"
                      @button-clicked="remind"
                    />
                  </div>
                  <a
                    :href="`https://tool.giardino-online.com/?language=${locale}`"
                    target="_blank"
                  >
                    <PrimeButton outlined class="w-full bg-white flex flex-center gap-2">
                      {{ t("product.compile") }}
                      <span class="i-mdi-open-in-new" />
                    </PrimeButton>
                  </a>
                </div>
              </div>

              <div
                v-if="selectedVariant?.stock === 0"
                class="mt-2 flex flex-col gap-2"
              >
                <div class="flex flex-row items-center gap-2">
                  <div>
                    <img src="~/assets/images/icons/attention.svg" alt="Attention">
                  </div>
                  <div class="text-lg font-bold">
                    {{ t("product.out_of_stock") }}
                  </div>
                </div>
                <div class="flex flex-row gap-2">
                  {{ t("product.mail") }}
                </div>
                <div class="w-full">
                  <PaintButton
                    :button-text="t('product.let_me_know')"
                    @button-clicked="remind"
                  />
                </div>
              </div>
            </div>
            <div v-else class="rounded-lg flex flex-col gap-4 text-center">
              <a href="#select_variant">
                <PaintButton :button-text="t('product.variants')" />
              </a>
              <a
                :href="`https://tool.giardino-online.com/?language=${locale}`"
                target="_blank"
              >
                <PrimeButton outlined class="w-full bg-white flex flex-center gap-2">
                  {{ t("product.compile") }}
                  <span class="i-mdi-open-in-new" />
                </PrimeButton>
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.4s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

[type='checkbox'].checkmark-gray:checked {
  background-image: url("data:image/svg+xml,%3csvg viewBox='0 0 16 16' fill='%23dc7818' stroke='DC7818' xmlns='http://www.w3.org/2000/svg'%3e%3cpath d='M12.207 4.793a1 1 0 010 1.414l-5 5a1 1 0 01-1.414 0l-2-2a1 1 0 011.414-1.414L6.5 9.086l4.293-4.293a1 1 0 011.414 0z'/%3e%3c/svg%3e");
  @apply text-primary-light;
}
</style>
